import React from "react";
import { createRoot } from "react-dom/client";

import RecommendationArticles from "../components/articles-container/recommendation-articles";
import { initConfigIdObserver } from "./config-id-observer";
import {
  BasketPageLoadedEvent,
  LazyFragmentData,
  RecommendationData,
  RecommendationPayload,
  RecommendationRequest
} from "../types/fragment";
import { CustomerType } from "../types/shop-cookies";
import { RecommendationArticlesProps } from "../types/articles";
import { requestCookieConsent } from "./consent-cookie";
import { getCustomerType } from "./customer-type";

export function StartLazyLoader(data: RecommendationData, prsScopes: NodeListOf<Element>) {
  const isBasketPage = document.querySelector(".basket-page-container") !== null;
  const isPrivateCustomer = getCustomerType() === CustomerType.Private;
  let allowAiRecommendations = false;
  let basketEvent: BasketPageLoadedEvent | undefined;

  requestCookieConsent()
    .then((consent) => (allowAiRecommendations = consent.AIRecommendations ?? false))
    .catch((reason) => console.debug("request for CookieConsent failed", reason));

  const loadConfigById = (scope: Element, useDefaultId: boolean) => {
    const location = scope.getAttribute("data-location")!;
    const defaultConfigId = scope.getAttribute("data-default-config-id")!;
    const customConfigId = scope.getAttribute("data-config-id");

    const fragmentData: LazyFragmentData = {
      container: scope,
      apiUrl: data.apiUrl,
      l10n: data.l10n,
      location: location,
      defaultConfigId: defaultConfigId,
      customConfigId: customConfigId,
      useDefaultId: useDefaultId
    };

    const recoReqData: RecommendationRequest = {
      allowAiRecommendations: allowAiRecommendations,
      isPrivateCustomer: isPrivateCustomer,
      salesArticleNumbersToIgnore: basketEvent?.classicArticlesWithoutPersonalization?.map(
        (a) => a.salesArticleNumber
      ),
      freeShippingThresholdNet: basketEvent?.shippingCosts.freeShippingThresholdNet,
      basketTotalNetValue: basketEvent?.costs.basketTotalValueNet
    };

    console.debug("reco req data", recoReqData);
    fetchRecommendations(fragmentData, recoReqData);
  };

  const initObserver = (source: string) => {
    console.debug("init observer for:", source);
    initConfigIdObserver(
      [...prsScopes],
      (element) => loadConfigById(element, false),
      (remaining) => {
        remaining.forEach((element) => loadConfigById(element, true));
      }
    );
  };

  // if PRS is loaded on the BasketPage it will wait for the BasketPageLoaded event and then start the observer
  if (isBasketPage) {
    let eventReceived = false;

    shell?.messageBroker.subscribeTo(
      "ESSA.BasketPage.BasketPageLoaded",
      (payload) => {
        if (payload) {
          eventReceived = true;
          basketEvent = payload as BasketPageLoadedEvent;
          initObserver("BasketPageLoaded event");
        }
      },
      "ESPP2.ProductRecommendation"
    );

    setTimeout(() => {
      if (!eventReceived) {
        initObserver("using fallback without BasketPageLoaded event");
      }
    }, 3000);
  } else {
    initObserver("not a basket page");
  }
}

function fetchRecommendations(reqData: LazyFragmentData, recoReqData: RecommendationRequest) {
  const reqConfigId = reqData.useDefaultId ? reqData.defaultConfigId : reqData.customConfigId;
  const request = new Request(`${reqData.apiUrl}recommendations/${reqConfigId}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(recoReqData)
  });

  fetch(request)
    .then((response) => {
      if (!response.ok) throw new Error("Network response was not ok for id: ");
      return response.json();
    })
    .then((response: RecommendationPayload) => {
      if (response.recommendations?.length > 0 && response.currencyInformation) {
        initRecommendationArticlesRoot(response, reqData);
      } else {
        reqData.container.classList.add("hide-empty-container");
        throw new Error("No recommendation articles available for id: ");
      }
    })
    .catch((error) => {
      if (error instanceof Error) {
        console.warn(error.message + reqConfigId);
      }
    });
}

function initRecommendationArticlesRoot(
  payload: RecommendationPayload,
  fragmentData: LazyFragmentData
) {
  const props: RecommendationArticlesProps = {
    recommendations: payload.recommendations,
    currencyInformation: payload.currencyInformation,
    l10n: fragmentData.l10n,
    location: fragmentData.location,
    defaultConfigId: fragmentData.defaultConfigId,
    useDefaultId: fragmentData.useDefaultId,
    customConfigId: fragmentData.customConfigId
  };
  const rootRecommendationArticles = createRoot(fragmentData.container);
  rootRecommendationArticles.render(<RecommendationArticles {...props} />);
}

// only for local testing on halla page
function _simulateAddingRecoIdsByAbTasty(prsScopes: NodeListOf<Element>) {
  const _validId = "c5533846-2982-4b34-8cbb-c0443bc32af3";
  const _invalidId = "c5533846-2982-4b34-8cbb-c0443bc32af6";
  prsScopes.forEach((scope) => scope.setAttribute("data-config-id", _validId));
}
